import { coerceNumberProperty, NumberInput, coerceBooleanProperty, BooleanInput } from '@angular/cdk/coercion';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { RememberService } from '@app/core/services/remember.service';
import { debounceTime, Subject, Subscription } from 'rxjs';

@Component({
    selector: 'tt-pager',
    templateUrl: './pager.component.html',
    styleUrls: ['./pager.component.css'],
})
export class PagerComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public ttRememberId: string = '';

    @Input()
    public get ttRememberDebounceTime(): number {
        return this._rememberDeounceTime;
    }
    public set ttRememberDebounceTime(value: Number) {
        this._rememberDeounceTime = coerceNumberProperty(value);
    }
    private _rememberDeounceTime = 150;

    /**
     * @default true
     */
    @Input()
    public set ttPersistLocalOnly(value: BooleanInput) {
        this._persistLocalOnly = coerceBooleanProperty(value);
    }
    public get ttPersistLocalOnly(): boolean {
        return this._persistLocalOnly;
    }
    private _persistLocalOnly = true;

    /**
     * @default true
     */
    @Input()
    public set ttOnlyRemember(value: BooleanInput) {
        this._onlyRemember = coerceBooleanProperty(value);
    }
    public get ttOnlyRemember(): boolean {
        return this._onlyRemember;
    }
    private _onlyRemember = true;

    /**
     * @default 0
     */
    @Input()
    public get ttPageIndex(): number {
        return this._pageIndex;
    }
    public set ttPageIndex(value: NumberInput) {
        this._pageIndex = coerceNumberProperty(value);
    }
    private _pageIndex = 0;

    @Output()
    public ttPageIndexChange = new EventEmitter<number>();

    /**
     * @default 5
     */
    @Input()
    public get ttPageSize(): number {
        return this._pageSize;
    }
    public set ttPageSize(value: NumberInput) {
        this._pageSize = coerceNumberProperty(value);
    }
    private _pageSize = 5;

    @Output()
    public ttPageSizeChange = new EventEmitter<number>();

    @Input()
    public get ttTotalCount(): number {
        return this._totalCount;
    }
    public set ttTotalCount(value: NumberInput) {
        this._totalCount = coerceNumberProperty(value);
    }
    private _totalCount = 0;

    @Output()
    public ttTotalCountChange = new EventEmitter<number>();

    @Output()
    public ttPageChange = new EventEmitter<PageChangeEvent>();

    public remember$ = new Subject<void>();

    public change$ = new Subject();

    public destroy$ = new Subject<void>();

    private rememberSubscription?: Subscription;

    constructor(private remember: RememberService) {
        const paginatorIntl = new MatPaginatorIntl();
        paginatorIntl.firstPageLabel = 'Første side';
        paginatorIntl.previousPageLabel = 'Forrige side';
        paginatorIntl.nextPageLabel = 'Neste side';
        paginatorIntl.itemsPerPageLabel = 'Sidestørrelse';
    }

    public onPageChanged(event: PageEvent) {
        if (event.pageIndex !== this.ttPageIndex) {
            this.ttPageIndex = event.pageIndex;
            this.ttPageIndexChange.emit(this.ttPageIndex);
        }

        if (event.pageSize !== this.ttPageSize) {
            this.ttPageSize = event.pageSize;
            this.ttPageSizeChange.emit(this.ttPageSize);
        }

        if (event.length !== this.ttTotalCount) {
            this.ttTotalCount = event.length;
            this.ttTotalCountChange.emit(this.ttTotalCount);
        }

        this.ttPageChange.emit({ totalCount: this.ttTotalCount, pageSize: this.ttPageSize, pageIndex: this.ttPageIndex });
        this.remember$.next();
    }

    private rememberState() {
        const state: PageChangeEvent = { totalCount: this.ttTotalCount, pageIndex: this.ttPageIndex, pageSize: this.ttPageSize };
        this.setLocalStorageState(state);

        if (!this.ttPersistLocalOnly) {
            this.remember.remember(this.ttRememberId, JSON.stringify(state));
        }
    }

    private setLocalStorageState(state: PageChangeEvent) {
        const states = this.getLocalStorageState();

        states[this.ttRememberId] = state;

        localStorage.setItem('tt-pagination', JSON.stringify(states));
    }

    private getLocalStorageState(): { [key: string]: PageChangeEvent } {
        const json = localStorage.getItem('tt-pagination') ?? '{}';

        try {
            return JSON.parse(json);
        } catch (error) {
            return {};
        }
    }

    private async getAndSetLastUsedState() {
        const state = this.getLocalStorageState()[this.ttRememberId];

        if (!!state) {
            this.ttPageIndex = state.pageIndex;
            this.ttPageSize = state.pageSize;
            this.ttTotalCount = state.totalCount;

            this.ttPageIndexChange.emit(this.ttPageIndex);
            this.ttPageSizeChange.emit(this.ttPageSize);
        }

        try {
            const dbJsonState = (await this.remember.getLastStatus(this.ttRememberId))[0].variablevalue;
            const dbState = JSON.parse(dbJsonState);

            if (!!dbState) {
                this.ttPageIndex = dbState.pageIndex;
                this.ttPageSize = dbState.pageSize;
                this.ttTotalCount = dbState.totalCount;

                this.ttPageIndexChange.emit(this.ttPageIndex);
                this.ttPageSizeChange.emit(this.ttPageSize);
            }
        } catch (error) {
            console.error(error);
        }
    }

    ngOnInit(): void {
        if (!this.ttOnlyRemember) {
            this.getAndSetLastUsedState();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ttRememberDebounceTime']) {
            this.ttRememberDebounceTime = changes['ttRememberDebounceTime'].currentValue;
        }

        if (changes['ttPageIndex']) {
            this.ttPageIndex = changes['ttPageIndex'].currentValue;
        }

        if (changes['ttPageSize']) {
            this.ttPageSize = changes['ttPageSize'].currentValue;
        }

        if (changes['ttTotalCount']) {
            this.ttTotalCount = changes['ttTotalCount'].currentValue;
        }

        if (changes['ttRememberId'] || changes['ttRememberDebounceTime']) {
            if (!!this.rememberSubscription) {
                this.rememberSubscription.unsubscribe();
                delete this.rememberSubscription;
            }

            if (!!this.ttRememberId) {
                this.rememberSubscription = this.remember$.pipe(debounceTime(this.ttRememberDebounceTime)).subscribe({ next: () => this.rememberState() });
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.change$.complete();
        this.remember$.complete();
    }
}

export interface PageChangeEvent {
    totalCount: number;
    pageSize: number;
    pageIndex: number;
}
